import {
  Box,
  Button,
  Grid,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Custom3dotString from "../../../../../components/Custom3dotString";
import CustomButton from "../../../../../components/CustomButton";
import CustomModal from "../../../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../../../components/CustomTextField";
import CustomSelect from "../../../../../components/Select/Select";
import {
  getPetsCompliantSummaryWoLoader,
  updatePrescriptionByIdWoLoader,
} from "../../../../../redux/reducers/petSlice";
import { frequncyCheckList } from "../../../../../util/arrayList";
import { mealTypes, routeList } from "../../../../../util/dropList";
import { frequencyConEnum } from "../../../../../util/enum";
import { getRightPackageUom } from "../../../../../util/function";
import { refreshClinicStocksToSession } from "../../../../../util/refreshClinicStocksToSession";

const initValues = {
  plName: null,
  dosage: "",
  duration: "",
  frequency: [],
  meal: "",
  customFrequency: false,
  route: null,
  stockId: null,
  productId: null,
  serviceId: null,
  type: null,
  batchId: null,
  remark: "",
  sig: "",
  detailId: null,
};

const EditPrescriptionDrawer = ({ open, onClose, item }) => {
  const dispatch = useDispatch();
  const [values, setValues] = useState(initValues);
  const [isSaving, setIsSaving] = useState(false);

  const reqDet = useSelector((state) => state?.pet?.complaintSummary?.data);
  const appDet = reqDet?.appointment;
  const mandateDataRaw = sessionStorage.getItem("mandateData");
  const { clinicStocks, clinicDet } = useMemo(() => {
    if (!mandateDataRaw) return { clinicStocks: [], clinicDet: {} };
    try {
      const reqData = JSON.parse(mandateDataRaw);
      return {
        clinicStocks: Array.isArray(reqData?.stocks) ? reqData.stocks : [],
        clinicDet: reqData?.clinic ?? {},
      };
    } catch {
      return { clinicStocks: [], clinicDet: {} };
    }
  }, [mandateDataRaw]);

  const getStockById = (stockId) =>
    clinicStocks?.find((stc) => stc?.stockId === stockId) ?? null;

  const getBatch = (stockId, batchId = null) => {
    if (!stockId) return null;
    const reqStock = getStockById(stockId);
    const clinicBatches = reqStock?.stockBatch?.filter(
      (btch) => btch?.createdBy === clinicDet?.id && btch?.approved,
    );
    if (!batchId) return clinicBatches?.[0] ?? null;
    return (
      clinicBatches?.find((bat) => bat?.batchId === batchId) ??
      clinicBatches?.[0] ??
      null
    );
  };

  const onChangeValue = (name, value) => {
    setValues((prev) => {
      const reqObj = { ...prev, [name]: value };
      if (name === "frequency" && value?.includes?.("Custom")) {
        reqObj.frequency = "";
        reqObj.customFrequency = true;
      }
      return reqObj;
    });
  };

  useEffect(() => {
    if (!open) {
      setValues(initValues);
      setIsSaving(false);
      return;
    }
    if (!item) return;

    const frequency =
      Array.isArray(item?.frequency) && item.frequency.length > 0
        ? item.frequency
        : Array.isArray(item?.frequencyDuration)
          ? item.frequencyDuration
              .map((freq) =>
                frequncyCheckList?.find((f) => f?.apiValue === freq),
              )
              ?.filter(Boolean)
          : [];

    setValues({
      plName: item?.plName ?? item?.name ?? null,
      dosage: item?.dosage ?? "",
      duration: item?.duration ?? "",
      frequency,
      meal: item?.meal ?? "",
      customFrequency: false,
      route:
        typeof item?.route === "object"
          ? item?.route
          : (routeList?.find(
              (rl) => rl?.name === item?.route || rl?.value === item?.route,
            ) ?? null),
      stockId: item?.stockId ?? null,
      productId: item?.productId ?? null,
      serviceId: item?.serviceId ?? null,
      type: item?.type ?? null,
      batchId: item?.batchId ?? null,
      remark: item?.remark ?? "",
      sig: item?.sig ?? "",
      detailId: item?.detailId ?? item?.id ?? null,
    });
  }, [item, open]);

  const onApiCall = () => {
    if (appDet?.appointmentId) {
      dispatch(getPetsCompliantSummaryWoLoader(appDet?.appointmentId));
    }
  };

  const buildPresPayloadItem = (pre) => {
    const selStock = clinicStocks?.find((stc) => stc?.stockId === pre?.stockId);
    const btchDet = selStock?.stockBatch?.filter(
      (btch) => btch?.createdBy === clinicDet?.id && btch?.approved,
    )?.[0];
    const reqFreqList =
      Array.isArray(pre?.frequency) && pre?.frequency?.length > 0
        ? pre?.frequency?.map((fre) => frequencyConEnum[fre?.value ?? fre])
        : (pre?.frequencyDuration ?? []);
    const reqQty = 1;
    const reqObj = {
      administered: false,
      type: pre?.type ?? null,
      stockId: pre?.stockId,
      batchId: btchDet?.batchId ?? pre?.batchId ?? null,
      name: pre?.plName ?? pre?.name,
      dosage: pre?.dosage ?? "",
      duration: pre?.duration ?? "",
      route: pre?.route?.value ?? pre?.route ?? null,
      meal: pre?.meal,
      quantity: reqQty,
      subUnit: "Unit",
      morning:
        (reqFreqList?.length > 0 && reqFreqList?.includes("morning")) ?? false,
      afternoon:
        (reqFreqList?.length > 0 && reqFreqList?.includes("afternoon")) ??
        false,
      night:
        (reqFreqList?.length > 0 && reqFreqList?.includes("night")) ?? false,
      adminChargeAmount: 0,
      adminCharge: "N",
      percentage: false,
      delete: false,
      discontinued: "N",
      detailId: pre?.detailId ?? null,
      remark: pre?.remark ?? "",
      drugSchedule: selStock?.drugSchedule ?? null,
      hsnSacCode: selStock?.hsnSacCode ?? null,
      packageUom: btchDet?.salesUom ?? pre?.packageUom ?? null,
      expiryDate: btchDet?.expiryDate ?? pre?.expiryDate ?? null,
      batchNo: btchDet?.batchNo ?? pre?.batchNo ?? null,
      shortName: selStock?.shortName ?? null,
    };
    const taxPercent =
      (btchDet?.cgst ? Number(btchDet?.cgst) : 0) +
      (btchDet?.sgst ? Number(btchDet?.sgst) : 0);
    const reqPercent = 100 + Number(taxPercent);
    const unitPrice =
      (btchDet?.unitSellingPrice ? Number(btchDet?.unitSellingPrice) : 0) /
      (reqPercent / 100);
    const taxValue =
      Number(
        (btchDet?.unitSellingPrice ? Number(btchDet?.unitSellingPrice) : 0) -
          Number(unitPrice),
      ) ?? 0;
    reqObj.price = btchDet?.unitSellingPrice ?? pre?.price ?? 0;
    reqObj.mrpPrice = btchDet?.unitSellingPrice
      ? Number(btchDet?.unitSellingPrice)
      : Number(pre?.mrpPrice ?? 0);
    reqObj.mrpDiscount = 0;
    reqObj.tax = Number(taxValue) * reqQty;
    reqObj.total =
      (btchDet?.unitSellingPrice ? Number(btchDet?.unitSellingPrice) : 0) *
        Number(reqQty) ?? 0;
    reqObj.frequencyDuration = reqFreqList ?? [];
    if (pre?.productId) reqObj.productId = pre?.productId;
    if (pre?.serviceId) reqObj.serviceId = pre?.serviceId;
    return reqObj;
  };

  const mapExistingPresToForm = (li) => {
    const reqObj = {
      detailId: li?.id ?? li?.detailId ?? null,
      plName: li?.name,
      name: li?.name,
      dosage: li?.dosage,
      duration: li?.duration,
      meal: li?.meal,
      type: li?.type,
      stockId: li?.stockId,
      productId: li?.productId,
      serviceId: li?.serviceId,
      batchId: li?.batchId,
      remark: li?.remark ?? "",
      route: routeList?.find((rl) => rl?.name === li?.route) ?? li?.route,
      frequency: [],
      frequencyDuration: li?.frequencyDuration ?? [],
      packageUom: li?.packageUom,
      expiryDate: li?.expiryDate,
      batchNo: li?.batchNo,
      price: li?.price,
      mrpPrice: li?.mrpPrice,
    };
    if (
      Array.isArray(li?.frequencyDuration) &&
      li.frequencyDuration.length > 0
    ) {
      reqObj.frequency = li.frequencyDuration
        .map((freq) => frequncyCheckList?.find((f) => f?.apiValue === freq))
        ?.filter(Boolean);
    }
    return reqObj;
  };

  const handleSave = () => {
    if (!item || !values?.detailId) return;
    const prescriptionId = reqDet?.prescription?.[0]?.prescriptionId;
    if (!prescriptionId) return;

    const existingDetails =
      reqDet?.prescription?.[0]?.prescriptionDetails ?? [];
    const existingMapped = existingDetails.map(mapExistingPresToForm);
    const updatedList = existingMapped.map((pre) =>
      pre?.detailId === values?.detailId ? { ...values, nameErr: false } : pre,
    );

    const prescription = updatedList.map(buildPresPayloadItem);
    const data = {
      status: "Pending",
      remark: reqDet?.prescription?.[0]?.remark ?? "",
      prescription,
      issue: "",
      paymentId: reqDet?.paymentId ?? null,
    };

    setIsSaving(true);
    dispatch(
      updatePrescriptionByIdWoLoader({
        prescriptionId,
        data,
      }),
    ).then((res) => {
      setIsSaving(false);
      if (res?.payload) {
        refreshClinicStocksToSession(dispatch);
        onApiCall();
        onClose();
      }
    });
  };

  const reqStck = values?.stockId ? getStockById(values?.stockId) : null;
  const packageUomLabel = getRightPackageUom(
    getBatch(values?.stockId, values?.batchId)?.packageUom,
    true,
  );
  const dosageField = (
    <CustomTextField
      fullWidth
      handleChange={(e) => onChangeValue("dosage", e?.target?.value)}
      value={values?.dosage}
      suffix={
        packageUomLabel?.length > 0 ? (
          <Custom3dotString
            str={`${packageUomLabel}`}
            length={9}
            className="fs12 font-medium"
            capitalize={false}
          />
        ) : (
          ""
        )
      }
      placeholder="e.g. 15 mg/kg"
    />
  );

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      header="Edit medication"
      modal
      modalWidth={40}
      reduceHeaderSize
    >
      <Box
        display="flex"
        flexDirection="column"
        sx={{ height: "calc(100vh - 75px)" }}
      >
        <Box p={3} flexGrow={1} overflow="auto">
          <Box mb={3}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                color: "#616161",
                letterSpacing: "0.5px",
                mb: 1,
                textTransform: "uppercase",
              }}
            >
              DRUG / MEDICATION
            </Typography>
            <TextField
              fullWidth
              size="small"
              disabled
              value={values?.plName || ""}
              sx={{
                backgroundColor: "#f5f5f5",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "6px",
                },
              }}
            />
            <Typography sx={{ fontSize: "12px", color: "#757575", mt: 0.8 }}>
              Linked to the formulary — remove and re-add via search to change
              the drug itself.
            </Typography>

            {reqStck?.drugSchedule || item?.drugSchedule ? (
              <Box mt={1} display="flex" alignItems="center" gap={0.8}>
                <Box
                  sx={{
                    backgroundColor: "#ffebee",
                    color: "#d32f2f",
                    fontSize: "11px",
                    fontWeight: 600,
                    px: 1,
                    py: 0.3,
                    borderRadius: "4px",
                    border: "1px solid #ffcdd2",
                  }}
                >
                  Controlled substance
                </Box>
                <Typography sx={{ fontSize: "12px", color: "#757575" }}>
                  — set on the inventory item, not editable here.
                </Typography>
              </Box>
            ) : null}
          </Box>

          <Grid container spacing={2} mb={2.5}>
            <Grid item xs={6}>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  color: "#616161",
                  letterSpacing: "0.5px",
                  mb: 1,
                  textTransform: "uppercase",
                }}
              >
                STRENGTH / DOSE
              </Typography>
              {values?.stockId ? (
                <Tooltip
                  title={
                    <>
                      <Typography className="fs12" display="block">
                        Approximated Dosage: {reqStck?.dosage ?? "-"}
                      </Typography>
                      <Typography className="fs12" display="block">
                        Dosage Type: {reqStck?.dosageType ?? "-"}
                      </Typography>
                    </>
                  }
                >
                  <span style={{ display: "inline-block", width: "100%" }}>
                    {dosageField}
                  </span>
                </Tooltip>
              ) : (
                dosageField
              )}
            </Grid>
            <Grid item xs={6}>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  color: "#616161",
                  letterSpacing: "0.5px",
                  mb: 1,
                  textTransform: "uppercase",
                }}
              >
                FREQUENCY
              </Typography>
              {values?.customFrequency ? (
                <CustomTextField
                  fullWidth
                  handleChange={(e) =>
                    onChangeValue("frequency", e?.target?.value)
                  }
                  value={values?.frequency}
                />
              ) : (
                <CustomSelect
                  list={frequncyCheckList}
                  value={values?.frequency ?? []}
                  handleChange={(e) =>
                    onChangeValue("frequency", e?.target?.value)
                  }
                  multiSelectSearchCheck
                />
              )}
            </Grid>
          </Grid>

          <Grid container spacing={2} mb={2.5}>
            <Grid item xs={6}>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  color: "#616161",
                  letterSpacing: "0.5px",
                  mb: 1,
                  textTransform: "uppercase",
                }}
              >
                ROUTE
              </Typography>
              <CustomSelect
                list={routeList}
                value={values?.route}
                search
                handleChange={(e, value) => onChangeValue("route", value)}
              />
            </Grid>

            <Grid item xs={6}>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  color: "#616161",
                  letterSpacing: "0.5px",
                  mb: 1,
                  textTransform: "uppercase",
                }}
              >
                DURATION (DAYS)
              </Typography>
              <CustomTextField
                fullWidth
                handleChange={(e) =>
                  onChangeValue("duration", e?.target?.value)
                }
                value={values?.duration}
              />
            </Grid>
          </Grid>

          <Box mb={2.5}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                color: "#616161",
                letterSpacing: "0.5px",
                mb: 1,
                textTransform: "uppercase",
              }}
            >
              INSTRUCTIONS
            </Typography>
            <CustomSelect
              list={mealTypes}
              value={values?.meal}
              select
              handleChange={(e) => onChangeValue("meal", e?.target?.value)}
            />
          </Box>

          {/* <Box mb={2.5}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                color: "#616161",
                letterSpacing: "0.5px",
                mb: 1,
                textTransform: "uppercase",
              }}
            >
              INSTRUCTIONS FOR OWNER (SIG)
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={2}
              placeholder="e.g. Give with food, complete the full course"
              value={values?.sig}
              onChange={(e) => onChangeValue("sig", e.target.value)}
              sx={{
                backgroundColor: "#fff",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "6px",
                },
              }}
            />
          </Box> */}

          <Box mb={2}>
            <Typography
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                color: "#616161",
                letterSpacing: "0.5px",
                mb: 1,
                textTransform: "uppercase",
              }}
            >
              REMARKS
            </Typography>
            <CustomTextField
              value={values?.remark}
              handleChange={(e) => onChangeValue("remark", e?.target?.value)}
              fullWidth
              placeholder="Notes (optional)"
            />
          </Box>
        </Box>

        <Box
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          gap={1.5}
          px={3}
          py={2}
          borderTop="1px solid #e0e0e0"
          backgroundColor="#fafafa"
        >
          <Button
            onClick={onClose}
            sx={{
              color: "#616161",
              textTransform: "none",
              fontWeight: 600,
              px: 2,
            }}
          >
            Cancel
          </Button>
          <CustomButton
            text="Save changes"
            smallBtn
            disabled={isSaving}
            onClick={handleSave}
          />
        </Box>
      </Box>
    </CustomModal>
  );
};

export default EditPrescriptionDrawer;
